import { css, html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { styleMap } from "lit/directives/style-map.js";
import { html as oghtml, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Command, CommandGroupSource, CommandQuery } from "@omegagrid/commands";
import rp, { CommandProvider } from "@reporting/rp-app-host";
import { Page } from "@reporting/rp-types/wiki";
import { pageComponentId } from "./helpers";

export type SearchResultCommand = Command & {
	props: {
		preview?: string;
	};
}

function highlightText(text: string, term: string): string {
	const m = (new RegExp(utils.escapeRegExp(term), 'gi'));
	if (m == null) return null;
	const matches: [number, number][] = [];
	let match: RegExpExecArray;
	while (match = m.exec(text)) {
		matches.push([match.index, match.index + term.length]);
	}
	return oghtml.highlightText(text, matches);
}

export class WikiSearchProvider implements CommandProvider {

	id = 'wiki';
	title = msg('Wiki');
	placeholder = msg('Search Wiki pages...');
	triggerCommand = 'w';
	lastQuery: CommandQuery;

	// 'app' / 'custom' select the searched buckets; 'semantic' adds the pages the server's vector model
	// finds close to the query (the server falls back to the text matches when no model is configured).
	// A guest has neither a custom wiki nor the semantic search (the server serves them the text matches only)
	switches = [
		{ key: 'app', value: msg('wiki-app'), checked: true },
		(rp.userProvider.isGuest() ? null : { key: 'custom', value: msg('wiki-custom'), checked: true }),
		(rp.userProvider.isGuest() ? null : { key: 'semantic', value: msg('wiki-semantic'), checked: true }),
	].filter(Boolean);

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		this.lastQuery = query;
		if (!query?.filter) return [];

		const switches = query.switches ?? this.switches.map(s => s.key);
		const buckets = switches.filter(s => s === 'app' || s === 'custom');
		if (buckets.length === 0) return [];
		const mode = switches.includes('semantic') ? 'semantic' : 'text';

		// the custom wiki is the one of the current accounting, so the request goes through it; without
		// it the request goes through accounting 0 = the application wiki, which is open to a guest too
		const accounting = buckets.includes('custom') ? undefined : 0;
		const pages = await rp.ms('wiki').get<Page[]>('/search', { q: query.filter, buckets: buckets.join(','), mode, accounting });
		if (pages.length === 0) return [];

		const app: CommandGroupSource = { id: 'app', name: msg('wiki-app'), commands: [] };
		const custom: CommandGroupSource = { id: 'custom', name: msg('wiki-custom'), commands: [] };

		for (const page of pages) {
			const own = buckets.includes('custom') && page.accounting === rp.accountingProvider.a;
			const group = own ? custom : app;
			group.commands.push({
				id: pageComponentId(own ? page.accounting : 0, page.id_wiki_page),
				name: page.name,
				group: group.id,
				props: { preview: page.content_preview },
				exec: (c) => {
					rp.host.open(c.id, { title: c.name });
				}
			});
		}

		return [app, custom].filter(g => g.commands.length > 0);
	}

	// TODO: markdownToHtml() is not possible here as it would need to be async
	markdownToString(content: string): string {
		if (!content) return '';
		// Odstranění markdown syntaxe
		let text = content
			// Odstranění nadpisů
			.replace(/^#{1,6}\s+/gm, '')
			// Odstranění tučného textu
			.replace(/\*\*(.*?)\*\*/g, '$1')
			.replace(/__(.*?)__/g, '$1')
			// Odstranění kurzívy
			.replace(/\*(.*?)\*/g, '$1')
			.replace(/_(.*?)_/g, '$1')
			// Odstranění kódu
			.replace(/`{3}[\s\S]*?`{3}/g, '')
			.replace(/`(.*?)`/g, '$1')
			// Odstranění obrázků ![ratio](url "caption") - zachovat caption
			.replace(/!\[[^\]]*\]\([^)]*?\s+"([^"]*)"\)/g, '$1')
			// Odstranění obrázků bez caption ![ratio](url)
			.replace(/!\[[^\]]*\]\([^)]+\)/g, '')
			// Odstranění odkazů [text](url)
			.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
			// Odstranění seznamů
			.replace(/^\s*[-*+]\s+/gm, '')
			.replace(/^\s*\d+\.\s+/gm, '')
			// Odstranění citací
			.replace(/^\s*>\s+/gm, '')
			// Odstranění horizontální čáry
			.replace(/^---+$/gm, '')
			// Odstranění tabulek (základní)
			.replace(/\|/g, ' ')
			// Normalizace bílých znaků
			.replace(/\s+/g, ' ')
			// odstranění zalomení řádků
			.replace(/<br>/g, ' ')
			.trim();

		return text;
	}

	getOptions = () => ({
		commandRenderer: (_div: HTMLDivElement, command: SearchResultCommand) => {
			const previewText = this.markdownToString(command.props?.preview);
			const detailHTML = this.lastQuery?.filter && previewText
				? highlightText(previewText, this.lastQuery.filter)
				: previewText;
			const nameHTML = command.name && this.lastQuery.filter
				? highlightText(command.name, this.lastQuery.filter)
				: command.name;

			return html`
				<div class="command" style="${styleMap({
					height: detailHTML ? null : '30px',
				})}">
					<div class="command-text">${unsafeHTML(nameHTML)}</div>
					${detailHTML ? html`<div class="command-detail">${unsafeHTML(detailHTML)}</div>` : null}
				</div>
			`;
		},
		itemHeight: 60,
		itemStyle: css`
			.item .command {
				display: flex;
				flex-direction: column;
				justify-content: center;
				height: 60px;
			}

			.item .command .command-text {
				flex: 0;
			}

			.item .command .command-detail {
				flex: 0 0 40px;
				font-size: 0.65em;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		`
	});

}
